import { useState, useEffect } from 'react';
import {
    Search,
    ChevronDown,
    ChevronUp,
    Sparkles,
    X,
    Mail,
    MessageSquare,
    CheckCircle,
    FileText,
    AlertCircle,
    Clock,
    Archive,
    CheckSquare,
    Check,
    ArrowRight,
    ListTodo,
    Reply,
    Trash2,
    RefreshCw,
} from 'lucide-react';
import { useAppState, InboxItem } from '../../contexts/AppContext';

// Source icon mapping
function getSourceIcon(source: InboxItem['source']) {
    switch (source) {
        case 'gmail':
            return <Mail className="w-4 h-4 text-[#EA4335]" />;
        case 'slack':
            return <MessageSquare className="w-4 h-4 text-[#4A154B]" />;
        case 'way2will':
            return <CheckCircle className="w-4 h-4 text-[#0D5F6B]" />;
        case 'notion':
            return <FileText className="w-4 h-4 text-[#1A2832]" />;
        case 'linear':
            return <AlertCircle className="w-4 h-4 text-[#5E6AD2]" />;
    }
}

// Newsletter detection patterns - emails from these senders are auto-bundled
const NEWSLETTER_PATTERNS = [
    // Common newsletter senders
    'newsletter', 'digest', 'noreply', 'no-reply', 'updates@', 'news@',
    'hello@', 'team@', 'weekly', 'daily', 'morning brew', 'substack',
    // Tech newsletters
    'techcrunch', 'verge', 'ars technica', 'hacker news', 'product hunt',
    // Product updates
    'figma', 'notion', 'linear', 'slack', 'asana', 'trello', 'github',
    // Marketing
    'mailchimp', 'convertkit', 'marketing', 'promo', 'offer', 'sale',
];

// Check if an inbox item is likely a newsletter
function isNewsletter(item: InboxItem): boolean {
    const senderLower = (item.sender || '').toLowerCase();
    const subjectLower = item.subject.toLowerCase();
    return NEWSLETTER_PATTERNS.some(pattern =>
        senderLower.includes(pattern) || subjectLower.includes(pattern)
    );
}

export default function AppInbox() {
    const { inboxItems, markInboxItemRead, deleteInboxItem, addTask, refreshGmailItems } = useAppState();
    const [showTriageSummary, setShowTriageSummary] = useState(true);
    const [activeFilter, setActiveFilter] = useState<string>('all');
    const [bundledExpanded, setBundledExpanded] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedInboxItem, setSelectedInboxItem] = useState<InboxItem | null>(null);
    const [newsletterSummary, setNewsletterSummary] = useState<string | null>(null);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshMessage, setRefreshMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

    // Separate newsletters from regular items
    const newsletterItems = inboxItems.filter(isNewsletter);
    const regularItems = inboxItems.filter(item => !isNewsletter(item));
    const bundledCount = newsletterItems.length;

    const filters = ['All', 'Urgent', 'Tasks', 'Emails', 'Slack'];

    // Refresh items on mount
    useEffect(() => {
        refreshGmailItems();
    }, [refreshGmailItems]);

    // Manual refresh handler with feedback
    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        setRefreshMessage({ text: 'Checking for new items...', type: 'info' });

        const prevCount = inboxItems.length;

        try {
            await refreshGmailItems();

            // Wait a moment for state to update, then check new count
            setTimeout(() => {
                const newCount = inboxItems.length;
                const diff = newCount - prevCount;

                if (diff > 0) {
                    setRefreshMessage({ text: `✓ ${diff} new item${diff > 1 ? 's' : ''} found!`, type: 'success' });
                } else {
                    setRefreshMessage({ text: '✓ Inbox is up to date', type: 'success' });
                }

                setIsRefreshing(false);

                // Clear message after 3 seconds
                setTimeout(() => setRefreshMessage(null), 3000);
            }, 500);
        } catch (error) {
            setRefreshMessage({ text: '✗ Failed to refresh. Try again.', type: 'error' });
            setIsRefreshing(false);
            setTimeout(() => setRefreshMessage(null), 4000);
        }
    };

    // Apply filters (only to regular items, newsletters are bundled separately)
    const filteredItems = regularItems.filter((item) => {
        // Search filter
        if (searchQuery && !item.subject.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }

        switch (activeFilter) {
            case 'urgent':
                return item.category === 'urgent';
            case 'tasks':
                return item.source === 'way2will';
            case 'emails':
                return item.source === 'gmail';
            case 'slack':
                return item.source === 'slack';
            default:
                return true;
        }
    });

    const urgentItems = filteredItems.filter((i) => i.category === 'urgent');
    const todayItems = filteredItems.filter((i) => i.category === 'today');
    const laterItems = filteredItems.filter((i) => i.category === 'later');

    const remainingCount = inboxItems.length + bundledCount;

    const toggleSelect = (id: string) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleArchiveAll = () => {
        if (selectedItems.length > 0) {
            selectedItems.forEach((id) => deleteInboxItem(id));
            setSelectedItems([]);
        } else {
            // Archive all visible items
            filteredItems.forEach((item) => deleteInboxItem(item.id));
        }
    };

    const handleMarkDone = () => {
        if (selectedItems.length > 0) {
            selectedItems.forEach((id) => markInboxItemRead(id));
            setSelectedItems([]);
        } else {
            filteredItems.forEach((item) => markInboxItemRead(item.id));
        }
    };

    const handleReviewBundled = () => {
        setBundledExpanded(true);
        setShowTriageSummary(false);
    };

    const handleConvertToTask = (item: InboxItem) => {
        addTask({
            title: item.subject,
            description: item.preview || '',
            completed: false,
            priority: item.priority || 'normal',
            context: item.source === 'gmail' ? 'Email' : item.source === 'slack' ? 'Slack' : 'Inbox',
            contextColor: item.source === 'gmail' ? '#EA4335' : item.source === 'slack' ? '#4A154B' : '#0D5F6B',
            contextBgColor: item.source === 'gmail' ? '#FEF3F2' : item.source === 'slack' ? '#F4E8F5' : '#D9EEF2',
            dueDate: new Date().toISOString().split('T')[0],
            subtasks: [],
        });
        deleteInboxItem(item.id);
        setSelectedInboxItem(null);
    };

    const handleOpenDetail = (item: InboxItem) => {
        setSelectedInboxItem(item);
        markInboxItemRead(item.id);
    };

    const handleCloseDetail = () => {
        setSelectedInboxItem(null);
    };

    // Simulate AI summary generation
    const handleGenerateSummary = () => {
        setIsGeneratingSummary(true);
        setTimeout(() => {
            setNewsletterSummary(
                `Bundled ${newsletterItems.length} newsletters. Highlights include tech updates from TechCrunch, product releases from Linear, and your weekly digest from Morning Brew.`
            );
            setIsGeneratingSummary(false);
        }, 1500);
    };

    const handleArchiveNewsletters = () => {
        if (confirm(`Archive all ${newsletterItems.length} newsletters?`)) {
            newsletterItems.forEach(item => deleteInboxItem(item.id));
        }
    };

    return (
        <div className="pt-4 leading-[1.5] dark:bg-dark-bg min-h-screen">
            {/* Header */}
            <div className="px-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                    <h1 className="text-xl font-semibold text-[#1A2832] dark:text-white">Inbox</h1>
                    <div className="flex items-center gap-2">
                        <div className="px-3 py-1 rounded-full bg-[#D9EEF2] dark:bg-dark-elevated">
                            <span className="text-xs font-semibold text-[#0D5F6B] dark:text-dark-teal-light">
                                {remainingCount} items
                            </span>
                        </div>
                        <button
                            onClick={handleManualRefresh}
                            disabled={isRefreshing}
                            className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated active:bg-[#EEF3F6] dark:active:bg-dark-border ${isRefreshing ? 'opacity-50' : ''}`}
                            title="Refresh inbox"
                        >
                            <RefreshCw className={`w-4 h-4 text-[#7B8C9B] ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={() => setShowSearch(!showSearch)}
                            className={`w-8 h-8 flex items-center justify-center rounded-full ${showSearch ? 'bg-[#0D5F6B] dark:bg-dark-teal' : 'bg-[#F5F8FA] dark:bg-dark-elevated'
                                } active:bg-[#EEF3F6] dark:active:bg-dark-border`}
                        >
                            <Search className={`w-4 h-4 ${showSearch ? 'text-white' : 'text-[#7B8C9B]'}`} />
                        </button>
                    </div>
                </div>

                {/* Refresh Status Toast */}
                {refreshMessage && (
                    <div className={`mb-3 px-4 py-3 rounded-xl flex items-center gap-2 animate-fade-in ${refreshMessage.type === 'success' ? 'bg-[#D5F2EA] text-[#1A6B5C]' :
                            refreshMessage.type === 'error' ? 'bg-[#FFEAE7] text-[#D66B5C]' :
                                'bg-[#D9EEF2] text-[#0D5F6B]'
                        }`}>
                        {refreshMessage.type === 'info' && <RefreshCw className="w-4 h-4 animate-spin" />}
                        <span className="text-sm font-medium">{refreshMessage.text}</span>
                    </div>
                )}

                {/* Search Bar */}
                {showSearch && (
                    <div className="mb-3">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search inbox..."
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                            autoFocus
                        />
                    </div>
                )}

                {/* Filter Chips */}
                <div className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                    {filters.map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setActiveFilter(filter.toLowerCase())}
                            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark ${activeFilter === filter.toLowerCase()
                                ? 'bg-[#0D5F6B] dark:bg-dark-teal text-white border-[#0D5F6B] dark:border-dark-teal'
                                : 'bg-white dark:bg-dark-card text-[#4A5C6A] dark:text-dark-text-secondary border-[#DCE4EA] dark:border-dark-border'
                                }`}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* AI Triage Summary */}
            {showTriageSummary && (
                <section className="px-4 mb-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#D9EEF2] dark:bg-dark-elevated flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-[18px] h-[18px] text-[#2A7A85]" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-[#1A2832] dark:text-dark-text mb-3">
                                    AI sorted 12 new items: <span className="text-[#D66B5C] font-semibold">3 urgent</span>, 6 newsletters bundled, 3 turned into tasks
                                </p>
                                <button
                                    onClick={handleReviewBundled}
                                    className="px-4 py-2 rounded-lg bg-[#F5F8FA] text-[#0D5F6B] text-sm font-semibold"
                                >
                                    Review Bundled Items
                                </button>
                            </div>
                            <button
                                onClick={() => setShowTriageSummary(false)}
                                className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                            >
                                <X className="w-[18px] h-[18px] text-[#7B8C9B]" />
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Floating Bulk Actions Bar - appears when items are selected */}
            {selectedItems.length > 0 && (
                <div className="fixed bottom-20 left-4 right-4 z-40 animate-slide-up">
                    <div className="bg-[#1A2832] rounded-2xl p-3 shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-white">
                                {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        // Select all visible items
                                        const allIds = filteredItems.map(i => i.id);
                                        setSelectedItems(allIds);
                                    }}
                                    className="px-3 py-1 rounded-lg bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={() => setSelectedItems([])}
                                    className="px-3 py-1 rounded-lg bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition"
                                >
                                    Deselect
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleArchiveAll}
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white text-[#1A2832] font-semibold text-sm hover:bg-gray-100 transition"
                            >
                                <Archive className="w-4 h-4" />
                                Archive
                            </button>
                            <button
                                onClick={handleMarkDone}
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#3DA58A] text-white font-semibold text-sm hover:bg-[#2D8A72] transition"
                            >
                                <CheckSquare className="w-4 h-4" />
                                Done
                            </button>
                            <button
                                onClick={() => {
                                    // Snooze selected items (move to later category)
                                    // For now, just clear selection - actual snooze would require context update
                                    setSelectedItems([]);
                                }}
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#E8A547] text-white font-semibold text-sm hover:bg-[#D69537] transition"
                            >
                                <Clock className="w-4 h-4" />
                                Snooze
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Urgent Section */}
            {urgentItems.length > 0 && (
                <section className="mb-4">
                    <div className="flex items-center gap-2 px-4 mb-3">
                        <span className="text-sm font-semibold text-[#1A2832] dark:text-white">Urgent</span>
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#FFEAE7] text-[#D66B5C]">
                            {urgentItems.length}
                        </span>
                    </div>
                    <div className="px-4">
                        <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                            {urgentItems.map((item, index) => (
                                <InboxItemRow
                                    key={item.id}
                                    item={item}
                                    showBorder={index < urgentItems.length - 1}
                                    isSelected={selectedItems.includes(item.id)}
                                    onToggleSelect={() => toggleSelect(item.id)}
                                    onClick={() => handleOpenDetail(item)}
                                    onQuickArchive={() => deleteInboxItem(item.id)}
                                    onQuickConvert={() => handleConvertToTask(item)}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Today Section */}
            {todayItems.length > 0 && (
                <section className="mb-4">
                    <div className="flex items-center gap-2 px-4 mb-3">
                        <span className="text-sm font-semibold text-[#1A2832] dark:text-white">Today</span>
                    </div>
                    <div className="px-4">
                        <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                            {todayItems.map((item, index) => (
                                <InboxItemRow
                                    key={item.id}
                                    item={item}
                                    showBorder={index < todayItems.length - 1}
                                    isSelected={selectedItems.includes(item.id)}
                                    onToggleSelect={() => toggleSelect(item.id)}
                                    onClick={() => handleOpenDetail(item)}
                                    onQuickArchive={() => deleteInboxItem(item.id)}
                                    onQuickConvert={() => handleConvertToTask(item)}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Later Section */}
            {laterItems.length > 0 && (
                <section className="mb-4">
                    <div className="flex items-center gap-2 px-4 mb-3">
                        <span className="text-sm font-semibold text-[#1A2832] dark:text-white">Later</span>
                    </div>
                    <div className="px-4">
                        <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                            {laterItems.map((item, index) => (
                                <InboxItemRow
                                    key={item.id}
                                    item={item}
                                    showBorder={index < laterItems.length - 1}
                                    isSelected={selectedItems.includes(item.id)}
                                    onToggleSelect={() => toggleSelect(item.id)}
                                    onClick={() => handleOpenDetail(item)}
                                    onQuickArchive={() => deleteInboxItem(item.id)}
                                    onQuickConvert={() => handleConvertToTask(item)}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Inbox Zero Celebration */}
            {filteredItems.length === 0 && !searchQuery && (
                <section className="px-4 mb-4">
                    <div className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] overflow-hidden">
                        {/* Confetti particles */}
                        <div className="absolute inset-0 pointer-events-none">
                            {[...Array(12)].map((_, i) => (
                                <div
                                    key={i}
                                    className="absolute w-2 h-2 rounded-full animate-confetti"
                                    style={{
                                        left: `${10 + (i * 7)}%`,
                                        top: '-10px',
                                        backgroundColor: ['#3DA58A', '#E8A547', '#0D5F6B', '#D66B5C', '#7B61C4'][i % 5],
                                        animationDelay: `${i * 0.15}s`,
                                        animationDuration: `${2 + (i % 3) * 0.5}s`,
                                    }}
                                />
                            ))}
                        </div>

                        {/* Success icon with pulse */}
                        <div className="relative inline-block mb-4">
                            <div className="absolute inset-0 bg-[#3DA58A]/20 rounded-full animate-ping" />
                            <div className="relative w-16 h-16 bg-[#3DA58A] rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                        </div>

                        <h3 className="text-2xl font-bold text-[#1A2832] mb-2">
                            🎉 Inbox Zero!
                        </h3>
                        <p className="text-base text-[#4A5C6A] mb-4">
                            You're all caught up! Time to focus on what matters.
                        </p>

                        {/* Motivational badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full shadow-sm">
                            <Sparkles className="w-4 h-4 text-[#E8A547]" />
                            <span className="text-sm font-medium text-[#4A5C6A]">
                                Productivity champion!
                            </span>
                        </div>
                    </div>
                </section>
            )}

            {/* Empty search results */}
            {filteredItems.length === 0 && searchQuery && (
                <section className="px-4 mb-4">
                    <div className="bg-white rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]">
                        <Search className="w-12 h-12 text-[#DCE4EA] mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-[#1A2832] mb-1">No results found</h3>
                        <p className="text-sm text-[#4A5C6A]">
                            No items match "{searchQuery}"
                        </p>
                    </div>
                </section>
            )}

            {/* Bundled Section */}
            <section className="mb-4 px-4">
                <button
                    onClick={() => setBundledExpanded(!bundledExpanded)}
                    className="w-full bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]"
                >
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[#1A2832]">
                                Bundled ({bundledCount} newsletters)
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-[#0D5F6B] font-semibold">
                                Read Later Summary
                            </span>
                            {bundledExpanded ? (
                                <ChevronUp className="w-4 h-4 text-[#7B8C9B]" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-[#7B8C9B]" />
                            )}
                        </div>
                    </div>
                </button>

                {bundledExpanded && (
                    <div className="mt-2 bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]">
                        {newsletterItems.length > 0 ? (
                            <>
                                {/* AI Summary Section */}
                                <div className="mb-4 p-3 bg-[#F5F8FA] rounded-xl border border-[#DCE4EA]">
                                    {newsletterSummary ? (
                                        <div className="flex gap-3">
                                            <Sparkles className="w-5 h-5 text-[#E8A547] flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-semibold text-[#1A2832] mb-1">AI Summary</p>
                                                <p className="text-sm text-[#4A5C6A] leading-relaxed">
                                                    {newsletterSummary}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="w-5 h-5 text-[#7B8C9B]" />
                                                <span className="text-sm text-[#7B8C9B]">Get a quick summary of these updates</span>
                                            </div>
                                            <button
                                                onClick={handleGenerateSummary}
                                                disabled={isGeneratingSummary}
                                                className="px-3 py-1.5 bg-white border border-[#DCE4EA] rounded-lg text-sm font-medium text-[#0D5F6B] hover:bg-[#F0F4F8] transition disabled:opacity-50"
                                            >
                                                {isGeneratingSummary ? 'Generating...' : 'Summarize'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Newsletter List */}
                                <div className="space-y-1 mb-4">
                                    {newsletterItems.map(item => (
                                        <div
                                            key={item.id}
                                            onClick={() => setSelectedInboxItem(item)}
                                            className="flex items-center justify-between p-2 hover:bg-[#F5F8FA] rounded-lg cursor-pointer group transition-colors"
                                        >
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className="w-8 h-8 rounded-full bg-[#E5EBF0] flex items-center justify-center flex-shrink-0 text-[#7B8C9B]">
                                                    {getSourceIcon(item.source)}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-[#1A2832] truncate">{item.sender}</p>
                                                    <p className="text-xs text-[#7B8C9B] truncate">{item.subject}</p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-[#7B8C9B] flex-shrink-0">
                                                {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Archive All Action */}
                                <button
                                    onClick={handleArchiveNewsletters}
                                    className="w-full py-2 flex items-center justify-center gap-2 text-sm font-medium text-[#7B8C9B] hover:text-[#D66B5C] hover:bg-[#FFF5F5] rounded-xl transition-colors"
                                >
                                    <Archive className="w-4 h-4" />
                                    Archive All Newsletters
                                </button>
                            </>
                        ) : (
                            <p className="text-center text-sm text-[#7B8C9B] py-4">
                                No newsletters found.
                            </p>
                        )}
                    </div>
                )}
            </section>

            {/* Bulk Actions */}
            <section className="px-4 pb-4">
                <div className="flex gap-2">
                    <button
                        onClick={handleArchiveAll}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white border border-[#DCE4EA] shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] text-[#0D5F6B]"
                    >
                        <Archive className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                            {selectedItems.length > 0 ? `Archive (${selectedItems.length})` : 'Archive All'}
                        </span>
                    </button>
                    <button
                        onClick={handleMarkDone}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white border border-[#DCE4EA] shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] text-[#0D5F6B]"
                    >
                        <CheckSquare className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                            {selectedItems.length > 0 ? `Mark Done (${selectedItems.length})` : 'Mark Done'}
                        </span>
                    </button>
                </div>
            </section>

            {/* Inbox Detail Modal */}
            {selectedInboxItem && (
                <InboxDetailModal
                    item={selectedInboxItem}
                    onClose={handleCloseDetail}
                    onMarkRead={() => markInboxItemRead(selectedInboxItem.id)}
                    onArchive={() => {
                        deleteInboxItem(selectedInboxItem.id);
                        setSelectedInboxItem(null);
                    }}
                    onConvertToTask={() => handleConvertToTask(selectedInboxItem)}
                />
            )}
        </div>
    );
}

// Inbox Item Row Component
interface InboxItemRowProps {
    item: InboxItem;
    showBorder: boolean;
    isSelected: boolean;
    onToggleSelect: () => void;
    onClick: () => void;
    onQuickArchive: () => void;
    onQuickConvert: () => void;
}

function InboxItemRow({ item, showBorder, isSelected, onToggleSelect, onClick, onQuickArchive, onQuickConvert }: InboxItemRowProps) {
    const [showActions, setShowActions] = useState(false);

    return (
        <div
            className={`flex items-start gap-3 px-4 py-4 active:bg-[#F5F8FA] transition cursor-pointer ${showBorder ? 'border-b border-[#E5EBF0]' : ''
                } ${isSelected ? 'bg-[#D9EEF2]/30' : ''}`}
            onClick={onClick}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {/* Selection Checkbox */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect();
                }}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition ${isSelected
                    ? 'bg-[#0D5F6B] border-[#0D5F6B]'
                    : 'border-[#DCE4EA] bg-white hover:border-[#0D5F6B]'
                    }`}
            >
                {isSelected && <Check className="w-3 h-3 text-white" />}
            </button>

            {/* Source Icon */}
            <div className="w-10 h-10 rounded-full bg-[#F5F8FA] flex items-center justify-center flex-shrink-0">
                {getSourceIcon(item.source)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    {item.sender && (
                        <span className="text-sm font-semibold text-[#1A2832]">{item.sender}</span>
                    )}
                    {item.channel && (
                        <span className="text-sm font-semibold text-[#1A2832]">{item.channel}</span>
                    )}
                    {item.timestamp && (
                        <span className="text-xs text-[#7B8C9B]">{item.timestamp}</span>
                    )}
                </div>
                <p className={`text-sm truncate ${item.read ? 'text-[#7B8C9B]' : 'text-[#1A2832]'}`}>
                    {item.subject}
                </p>
                {item.dueTime && (
                    <div className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 text-[#7B8C9B]" />
                        <span className="text-xs text-[#7B8C9B]">{item.dueTime}</span>
                        {item.priority === 'medium' && (
                            <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-[#FFF4E5] text-[#E8A547]">
                                Medium
                            </span>
                        )}
                    </div>
                )}
                {item.aiLabel && (
                    <div className="flex items-center gap-1 mt-1">
                        <Sparkles className="w-3 h-3 text-[#0D5F6B]" />
                        <span className="text-xs text-[#0D5F6B]">{item.aiLabel}</span>
                    </div>
                )}
            </div>

            {/* Quick Actions (visible on hover) */}
            {showActions && (
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onQuickConvert();
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#D9EEF2] transition"
                        title="Convert to Task"
                    >
                        <ListTodo className="w-4 h-4 text-[#0D5F6B]" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onQuickArchive();
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F5F8FA] transition"
                        title="Archive"
                    >
                        <Archive className="w-4 h-4 text-[#7B8C9B]" />
                    </button>
                </div>
            )}

            {/* Arrow indicator */}
            {!showActions && (
                <ArrowRight className="w-4 h-4 text-[#DCE4EA] flex-shrink-0 mt-2" />
            )}
        </div>
    );
}

// Inbox Detail Modal
interface InboxDetailModalProps {
    item: InboxItem;
    onClose: () => void;
    onMarkRead: () => void;
    onArchive: () => void;
    onConvertToTask: () => void;
}

function InboxDetailModal({ item, onClose, onMarkRead, onArchive, onConvertToTask }: InboxDetailModalProps) {
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-[#EEF3F6] flex-shrink-0">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#F5F8FA] flex items-center justify-center">
                                {getSourceIcon(item.source)}
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#1A2832]">
                                    {item.sender || item.channel || 'Inbox Item'}
                                </h2>
                                <span className="text-xs text-[#7B8C9B]">{item.timestamp}</span>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                        >
                            <X className="w-5 h-5 text-[#7B8C9B]" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    <h3 className="text-base font-semibold text-[#1A2832] mb-3">{item.subject}</h3>

                    {item.preview && (
                        <p className="text-sm text-[#4A5C6A] mb-4 leading-relaxed">{item.preview}</p>
                    )}

                    {!item.preview && (
                        <p className="text-sm text-[#7B8C9B] italic mb-4">No preview available</p>
                    )}

                    {/* Metadata */}
                    <div className="space-y-2 mb-4">
                        {item.dueTime && (
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4 text-[#7B8C9B]" />
                                <span className="text-[#4A5C6A]">{item.dueTime}</span>
                            </div>
                        )}
                        {item.priority && (
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${item.priority === 'high' ? 'bg-[#FFEAE7] text-[#D66B5C]' :
                                    item.priority === 'medium' ? 'bg-[#FFF4E5] text-[#E8A547]' :
                                        'bg-[#F5F8FA] text-[#7B8C9B]'
                                    }`}>
                                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)} Priority
                                </span>
                            </div>
                        )}
                        {item.aiLabel && (
                            <div className="flex items-center gap-2 text-sm">
                                <Sparkles className="w-4 h-4 text-[#0D5F6B]" />
                                <span className="text-[#0D5F6B]">{item.aiLabel}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-[#EEF3F6] flex-shrink-0 space-y-3">
                    <div className="flex gap-2">
                        <button
                            onClick={onConvertToTask}
                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0D5F6B] text-white font-semibold"
                        >
                            <ListTodo className="w-4 h-4" />
                            Convert to Task
                        </button>
                        {item.source === 'gmail' && (
                            <button
                                onClick={onClose}
                                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-[#0D5F6B] text-[#0D5F6B] font-semibold"
                            >
                                <Reply className="w-4 h-4" />
                                Reply
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onMarkRead}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#F5F8FA] text-[#4A5C6A] text-sm font-semibold"
                        >
                            <CheckCircle className="w-4 h-4" />
                            {item.read ? 'Mark Unread' : 'Mark Read'}
                        </button>
                        <button
                            onClick={onArchive}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#F5F8FA] text-[#4A5C6A] text-sm font-semibold"
                        >
                            <Archive className="w-4 h-4" />
                            Archive
                        </button>
                        <button
                            onClick={onArchive}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#FFEAE7] text-[#D66B5C] text-sm font-semibold"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
